import {
  onAuthStateChanged,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signOut,
  updatePassword,
} from "firebase/auth";
import { collection, doc, getDoc, getDocs, serverTimestamp, writeBatch } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { auth, db, functions } from "../../../firebase.js";
import { normalizeBanquetOwner } from "../model/banquetOwnerModel.js";
import { assertSuperAdminAccess, BANQUET_COLLECTION, BANQUET_OWNER_COLLECTION } from "./banquetAccessRules.js";

const getOwnerDocRef = (uid) => doc(db, BANQUET_OWNER_COLLECTION, uid);
const getBanquetDocRef = (banquetId) => doc(db, BANQUET_COLLECTION, banquetId);

const waitForFirebaseAuthUser = () =>
  new Promise((resolve, reject) => {
    const unsubscribe = onAuthStateChanged(
      auth,
      (firebaseUser) => {
        unsubscribe();
        resolve(firebaseUser);
      },
      (error) => {
        unsubscribe();
        reject(error);
      }
    );
  });

const buildOwnerFromSnapshot = (snapshot) => {
  if (!snapshot.exists()) {
    return null;
  }

  return normalizeBanquetOwner({
    uid: snapshot.id,
    ...snapshot.data(),
  });
};

const getOwnerProfileByUid = async (uid) => {
  if (!uid) {
    return null;
  }

  const snapshot = await getDoc(getOwnerDocRef(uid));
  return buildOwnerFromSnapshot(snapshot);
};

const createFirebaseAuthError = (error) => {
  if (error?.code === "auth/invalid-credential" || error?.code === "auth/wrong-password" || error?.code === "auth/user-not-found") {
    return new Error("Invalid email or password.");
  }

  if (error?.code === "auth/too-many-requests") {
    return new Error("Too many login attempts. Try again later.");
  }

  if (error?.code === "auth/email-already-in-use") {
    return new Error("This owner email is already in use.");
  }

  return error instanceof Error ? error : new Error("Auth request failed.");
};

export const loginBanquetOwner = async ({ email, password }) => {
  if (!email || !password) {
    const error = new Error("Email and password are required.");
    error.code = "banquet-owner/missing-credentials";
    throw error;
  }

  try {
    const credentials = await signInWithEmailAndPassword(auth, email.trim(), password);
    const owner = await getOwnerProfileByUid(credentials.user.uid);

    if (!owner) {
      await signOut(auth);
      const error = new Error("This account does not have a banquet owner profile.");
      error.code = "banquet-owner/profile-not-found";
      throw error;
    }

    if (owner.isActive === false) {
      await signOut(auth);
      const error = new Error("This banquet owner account is inactive.");
      error.code = "banquet-owner/account-inactive";
      throw error;
    }

    return owner;
  } catch (error) {
    throw createFirebaseAuthError(error);
  }
};

export const logoutBanquetOwner = async () => {
  await signOut(auth);
};

export const getCurrentBanquetOwner = async () => {
  const currentUser = auth.currentUser || await waitForFirebaseAuthUser();
  if (!currentUser) {
    return null;
  }

  return getOwnerProfileByUid(currentUser.uid);
};

export const subscribeToBanquetOwnerAuth = (callback) => {
  return onAuthStateChanged(auth, async (firebaseUser) => {
    if (typeof callback !== "function") {
      return;
    }

    if (!firebaseUser) {
      callback(null);
      return;
    }

    try {
      const owner = await getOwnerProfileByUid(firebaseUser.uid);
      callback(owner && owner.isActive !== false ? owner : null);
    } catch (error) {
      callback(null, error);
    }
  });
};

const createCallableError = (error) => {
  if (typeof error?.code === "string" && error.code.startsWith("functions/")) {
    return new Error(error.message || "Unable to create banquet user.");
  }

  return error instanceof Error ? error : new Error("Unable to create banquet user.");
};

export const createBanquetOwnerAccount = async (payload) => {
  try {
    const callCreateBanquetOwnerAccount = httpsCallable(functions, "createBanquetOwnerAccount");
    const response = await callCreateBanquetOwnerAccount(payload);
    return response.data;
  } catch (error) {
    throw createCallableError(error);
  }
};

export const listBanquetOwnerAccounts = async () => {
  const actingOwner = await getCurrentBanquetOwner();
  assertSuperAdminAccess(actingOwner);

  const snapshot = await getDocs(collection(db, BANQUET_OWNER_COLLECTION));
  return snapshot.docs
    .map((item) =>
      normalizeBanquetOwner({
        uid: item.id,
        ...item.data(),
      })
    )
    .sort((left, right) => String(left.email || "").localeCompare(String(right.email || "")));
};

export const updateBanquetOwnerAccountStatus = async ({ uid, isActive }) => {
  const actingOwner = await getCurrentBanquetOwner();
  assertSuperAdminAccess(actingOwner);

  const nextActive = Boolean(isActive);
  const targetOwner = await getOwnerProfileByUid(uid);
  const assignedBanquetIds = targetOwner?.assignedBanquetIds || [];

  const batch = writeBatch(db);
  batch.update(getOwnerDocRef(uid), {
    isActive: nextActive,
    active: nextActive,
    updatedAt: serverTimestamp(),
    updatedBy: actingOwner.uid || actingOwner.email || "super_admin",
  });

  for (const banquetId of assignedBanquetIds) {
    const banquetSnapshot = await getDoc(getBanquetDocRef(banquetId));
    if (banquetSnapshot.exists()) {
      batch.update(getBanquetDocRef(banquetId), {
        isActive: nextActive,
        updatedAt: serverTimestamp(),
      });
    }
  }

  await batch.commit();

  return getOwnerProfileByUid(uid);
};

export const completeMandatoryBanquetOwnerPasswordChange = async ({ newPassword }) => {
  if (!newPassword || newPassword.length < 6) {
    const error = new Error("New password must be at least 6 characters.");
    error.code = "banquet-owner/weak-password";
    throw error;
  }

  const currentUser = auth.currentUser;
  if (!currentUser) {
    const error = new Error("You must be signed in to change your password.");
    error.code = "banquet-owner/not-signed-in";
    throw error;
  }

  try {
    await updatePassword(currentUser, newPassword);

    const callCompleteMandatoryPasswordChange = httpsCallable(functions, "completeMandatoryPasswordChange");
    await callCompleteMandatoryPasswordChange({});
  } catch (error) {
    if (error?.code === "auth/requires-recent-login") {
      const wrapped = new Error("For security, please sign in again before changing your password.");
      wrapped.code = "banquet-owner/requires-recent-login";
      throw wrapped;
    }

    throw createCallableError(error);
  }
};

export const resetBanquetOwnerPassword = async ({ email }) => {
  if (!email) {
    const error = new Error("Owner email is required.");
    error.code = "banquet-owner/missing-email";
    throw error;
  }

  try {
    await sendPasswordResetEmail(auth, email.trim());
    return { success: true };
  } catch (error) {
    throw createFirebaseAuthError(error);
  }
};
